import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { createClient } from '@supabase/supabase-js'
import { Loader2, AlertCircle } from 'lucide-react'
import { useDocumentTitle } from '@/hooks/use-document-title'
import '@/lib/supabase' // garante que a tipagem global de window.__impersonationClient está disponível

export const Route = createFileRoute('/auth/impersonate')({
  component: ImpersonatePage,
})

// Client isolado que usa sessionStorage em vez de localStorage.
// sessionStorage é per-tab: não é compartilhado com outras abas,
// então a sessão do super admin no localStorage não é afetada.
function createIsolatedClient() {
  return createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
        storage: window.sessionStorage,
        storageKey: 'sb-impersonation-auth-token',
      },
    }
  )
}

function ImpersonatePage() {
  useDocumentTitle('Entrando como usuário...')
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const processImpersonation = async () => {
      try {
        const params = new URLSearchParams(window.location.search)
        const token = params.get('token')

        if (!token) {
          setError('Token de impersonação não fornecido')
          return
        }

        // Usa um client temporário sem persistência para verificar o token
        // sem tocar em nenhum storage ainda.
        const tempClient = createClient(
          import.meta.env.VITE_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_ANON_KEY,
          { auth: { autoRefreshToken: false, persistSession: false, detectSessionInUrl: false } }
        )

        const { data, error: verifyError } = await tempClient.auth.verifyOtp({
          token_hash: token,
          type: 'magiclink',
        })

        if (verifyError) {
          console.error('Impersonation verify error:', verifyError)
          setError(verifyError.message || 'Token inválido ou expirado')
          return
        }

        if (!data?.session) {
          setError('Não foi possível estabelecer a sessão')
          return
        }

        // Cria o client isolado (sessionStorage) e estabelece a sessão do usuário impersonado.
        // Isso NÃO afeta o localStorage, então a aba do super admin continua intacta.
        const isolatedClient = createIsolatedClient()
        window.__impersonationClient = isolatedClient

        await isolatedClient.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        })

        // Marca que esta aba está em modo de impersonação.
        // O AuthProvider lerá isso para usar o client isolado.
        sessionStorage.setItem('is_impersonating', 'true')

        // Recarrega a página para que o AuthProvider inicialize com o client isolado.
        window.location.replace('/dashboard')
      } catch (err) {
        console.error('Impersonation error:', err)
        setError('Erro ao processar impersonação')
      }
    }

    processImpersonation()
  }, [navigate])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Erro na Impersonação</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.close()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fechar janela
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center">
        <Loader2 className="w-10 h-10 animate-spin mx-auto text-teal-600 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Entrando como usuário</h1>
        <p className="text-gray-600">Aguarde enquanto estabelecemos a sessão...</p>
      </div>
    </div>
  )
}
